import { useEffect, useMemo, useState } from "react";
import { navItems, feedItems, trackingEvents } from "@/mocks/siteData";

interface ResultGroup {
  group: string;
  anchor: string;
  label: string;
  meta: string;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [searchOpen]);

  const results: ResultGroup[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const feed = feedItems
      .filter((it) => (it.title + it.tag + it.summary).toLowerCase().includes(q))
      .map((it) => ({ group: "最新动态", anchor: "feed", label: it.title, meta: it.tag }));
    const track = trackingEvents
      .filter((it) => (it.name + it.category).toLowerCase().includes(q))
      .map((it) => ({ group: "正在追踪", anchor: "tracking", label: it.name, meta: it.category }));
    return [...feed, ...track].slice(0, 8);
  }, [query]);

  const goTo = (id: string) => {
    setSearchOpen(false);
    setMenuOpen(false);
    setQuery("");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  const light = !scrolled && !menuOpen && !searchOpen;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          light
            ? "bg-gradient-to-b from-black/50 to-transparent py-4 text-background-50"
            : "border-b border-background-200/70 bg-background-50/95 py-2 text-foreground-900 shadow-soft backdrop-blur"
        }`}
      >
        <nav className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 md:px-6" aria-label="主导航">
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              goTo("top");
            }}
            className="flex items-center gap-2"
            aria-label="52爆料 回到顶部"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-black text-background-50">
              爆
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-black tracking-tight">52爆料</span>
              <span
                className={`text-[9px] tracking-[0.28em] ${light ? "text-background-200/80" : "text-foreground-600"}`}
              >
                追更后续站
              </span>
            </span>
          </a>

          <ul className="hidden items-center gap-1 lg:flex" role="menu">
            {navItems.slice(1).map((item) => (
              <li key={item.key} role="menuitem">
                <a
                  href={`#${item.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goTo(item.key);
                  }}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    light
                      ? "text-background-100/90 hover:bg-white/10"
                      : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSearchOpen(true);
                setMenuOpen(false);
              }}
              aria-label="搜索站内内容"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                light ? "hover:bg-white/10" : "hover:bg-background-100"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>
            <a
              href="#subscribe"
              onClick={(e) => {
                e.preventDefault();
                goTo("subscribe");
              }}
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              订阅追更
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="展开菜单"
              aria-expanded={menuOpen}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors lg:hidden ${
                light ? "hover:bg-white/10" : "hover:bg-background-100"
              }`}
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
            </button>
          </div>
        </nav>

        {menuOpen ? (
          <div className="border-t border-background-200/70 bg-background-50 lg:hidden">
            <ul className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
              {navItems.slice(1).map((item) => (
                <li key={item.key}>
                  <a
                    href={`#${item.key}`}
                    onClick={(e) => {
                      e.preventDefault();
                      goTo(item.key);
                    }}
                    className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <a
                  href="#subscribe"
                  onClick={(e) => {
                    e.preventDefault();
                    goTo("subscribe");
                  }}
                  className="inline-flex w-full items-center justify-center rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
                >
                  订阅追更提醒
                </a>
              </li>
            </ul>
          </div>
        ) : null}
      </header>

      {searchOpen ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground-950/60 px-4 pt-[12vh] backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft">
            <div className="flex items-center gap-3 border-b border-background-200/70 px-4 py-3">
              <i className="ri-search-line text-lg text-primary-600" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索正在追踪的事件或最新进展…"
                aria-label="搜索框"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="关闭搜索"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="max-h-[50vh] overflow-auto p-2">
              {!query.trim() ? (
                <p className="px-3 py-6 text-center text-sm text-foreground-500">
                  输入事件名、栏目名或关键词，帮你快速定位更新板块。
                </p>
              ) : results.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-foreground-500">没有找到匹配内容，换个关键词试试。</p>
              ) : (
                <ul className="divide-y divide-background-100">
                  {results.map((r, idx) => (
                    <li key={`${r.anchor}-${idx}`}>
                      <button
                        type="button"
                        onClick={() => goTo(r.anchor)}
                        className="flex w-full items-start gap-3 px-3 py-3 text-left hover:bg-background-100"
                      >
                        <span className="mt-0.5 inline-flex h-6 shrink-0 items-center rounded bg-primary-500 px-2 text-[11px] font-bold text-background-50">
                          {r.group}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-foreground-900">{r.label}</span>
                          <span className="block text-xs text-foreground-500">{r.meta}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}